import React, { useState } from "react";
import { Box, Typography, IconButton, Avatar, Badge, Menu, MenuItem, ListItemIcon } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import GroupIcon from "@mui/icons-material/Groups";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SettingsIcon from "@mui/icons-material/Settings";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Conversation } from "@/types/chat";
import { useAuth } from "@/hooks/auth";
import ControllableConfirmation from "@/components/confirmation/controllable.confirmation";

interface ChatWindowHeaderProps {
	conversation: Conversation;
	typingUsers: string[];
	onClose: () => void;
	onMinimize?: () => void;
	isMinimized?: boolean;
	unreadCount?: number;
	onAddMemberClick?: () => void;
	onManageGroupClick?: () => void;
	onDeleteChat?: () => void;
}

export const ChatWindowHeader: React.FC<ChatWindowHeaderProps> = ({
	conversation,
	typingUsers,
	onClose,
	onMinimize,
	isMinimized,
	unreadCount,
	onAddMemberClick,
	onManageGroupClick,
	onDeleteChat,
}) => {
	const theme = useTheme();
	const { user } = useAuth();
	
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	let displayName = conversation.name || "Chat";
	let displayAvatar = "";
	let isOnline = false;
	let isGroup = conversation.type === "GROUP";

	if (isGroup) {
		displayName = conversation.name || "Group Chat";
	} else if (conversation.participants?.length > 0) {
		const otherUser =
			conversation.participants.find((p) => p.id !== user?.id) ||
			conversation.participants[0];
		const fullName =
			`${otherUser.firstName || ""} ${otherUser.lastName || ""}`.trim();
		displayName = fullName || otherUser.username || "Unknown";
		displayAvatar = otherUser.avatar || "";
		isOnline = Boolean((otherUser as any).isOnline);
	}

	const isOthersTyping = typingUsers.some((id) => id !== user?.id);

	return (
		<Box
			sx={{
				p: 2,
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				background:
					theme.palette.mode === "dark"
						? "rgba(255, 255, 255, 0.03)"
						: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
				borderBottom:
					theme.palette.mode === "dark"
						? "1px solid rgba(255,255,255,0.08)"
						: "none",
				color:
					theme.palette.mode === "dark"
						? theme.palette.text.primary
						: "primary.contrastText",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
				{isGroup ? (
					<Avatar
						sx={{
							width: 36,
							height: 36,
							bgcolor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.1)" : "primary.light",
							color: theme.palette.mode === 'dark' ? "#fff" : "primary.main",
							border:
								theme.palette.mode === "dark"
									? "2px solid rgba(255,255,255,0.1)"
									: "2px solid rgba(255,255,255,0.2)",
						}}
					>
						<GroupIcon fontSize="small" />
					</Avatar>
				) : (
					<Badge
						color="success"
						variant="dot"
						invisible={!isOnline}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						overlap="circular"
						sx={{
							"& .MuiBadge-badge": {
								border: `2px solid ${theme.palette.primary.main}`,
								width: 12,
								height: 12,
								borderRadius: "50%",
							},
						}}
					>
						<Avatar
							src={displayAvatar}
							sx={{
								width: 36,
								height: 36,
								border:
									theme.palette.mode === "dark"
										? "2px solid rgba(255,255,255,0.1)"
										: "2px solid rgba(255,255,255,0.2)",
							}}
						/>
					</Badge>
				)}
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
						<Typography
							variant="subtitle1"
							fontWeight="600"
							sx={{ 
								lineHeight: 1.2,
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis'
							}}
						>
							{displayName}
						</Typography>
						{unreadCount !== undefined && unreadCount > 0 && (
							<Box
								sx={{
									backgroundColor: theme.palette.error.main,
									color: theme.palette.error.contrastText,
									borderRadius: '10px',
									padding: '0 6px',
									fontSize: '0.75rem',
									fontWeight: 'bold',
									lineHeight: 1.5,
									flexShrink: 0,
								}}
							>
								{unreadCount > 99 ? '99+' : unreadCount}
							</Box>
						)}
					</Box>
					<Typography variant="caption" sx={{ opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
						{isOthersTyping
							? "Бичиж байна..."
							: isGroup
								? `${conversation.participants?.length || 0} гишүүн`
								: isOnline
									? "Онлайн"
									: "Офлайн"}
					</Typography>
				</Box>
			</Box>
			<Box sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
				<IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: "inherit", opacity: 0.8 }}>
					<MoreVertIcon fontSize="small" />
				</IconButton>
				{onMinimize && (
					<IconButton size="small" onClick={onMinimize} sx={{ color: "inherit", opacity: 0.8 }}>
						{isMinimized ? (
							<KeyboardArrowUpIcon fontSize="small" />
						) : (
							<KeyboardArrowDownIcon fontSize="small" />
						)}
					</IconButton>
				)}
				<IconButton size="small" onClick={onClose} sx={{ color: "inherit" }}>
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={() => setAnchorEl(null)}
				disableScrollLock={true}
				PaperProps={{
					sx: {
						minWidth: 160,
						boxShadow: theme.shadows[8],
						borderRadius: 2,
					}
				}}
			>
				{isGroup && (
					<MenuItem onClick={() => { setAnchorEl(null); onAddMemberClick?.(); }}>
						<ListItemIcon>
							<PersonAddIcon fontSize="small" />
						</ListItemIcon>
						Гишүүн нэмэх
					</MenuItem>
				)}
				{isGroup && (
					<MenuItem onClick={() => { setAnchorEl(null); onManageGroupClick?.(); }}>
						<ListItemIcon>
							<SettingsIcon fontSize="small" />
						</ListItemIcon>
						Групп тохиргоо
					</MenuItem>
				)}
				{!isGroup && (
					<MenuItem onClick={() => { 
						setAnchorEl(null); 
						setConfirmOpen(true);
					}}>
						<ListItemIcon>
							<DeleteOutlineIcon fontSize="small" color="error" />
						</ListItemIcon>
						<Typography color="error">Чат устгах</Typography>
					</MenuItem>
				)}
			</Menu>

			<ControllableConfirmation
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				title="Чат устгах"
				content="Энэ чатыг устгахдаа итгэлтэй байна уу? Устгасан тохиолдолд сэргээх боломжгүй."
				severity="error"
				confirmLabel="Устгах"
				onConfirm={async () => {
					setIsDeleting(true);
					try {
						await onDeleteChat?.();
					} finally {
						setIsDeleting(false);
						setConfirmOpen(false);
					}
				}}
				loading={isDeleting}
			/>
		</Box>
	);
};
